import CheckboxWTN from 'components/generic/CheckboxWTN'
import ModalWTN from 'components/generic/ModalWTN'
import styles from './actionPendingScoreModal.module.scss'
import i18n from 'constants/strings'
import { useModalDispatch, useModalState } from 'hooks/use-modal'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { ProfileModalsState } from '../panels/MyScoresPanel'
import { useDispatch, useSelector } from 'react-redux'
import { approvePending, rejectPending, deletePending } from 'actions/social-matches-actions'
import { AppState } from 'redux/reducers'
import { dontShowMeAgain } from 'utils/local-storage'
import { ProfileModalId } from 'constants/modals'
import moment from 'moment'
import { getSidesKeysRelativeToPlayer } from 'components/profile/generic/MatchCard'
import SidePlayers from 'components/profile/generic/SidePlayers'

export enum ActionPendingScoreModalType {
  APPROVE = 'approve-pending-score-modal',
  REJECT = 'reject-pending-score-modal',
  DELETE = 'delete-pending-score-modal',
}

const ActionPendingScoreModal = () => {
  const [isChecked, setIsChecked] = useState(false)
  const dispatch = useDispatch()
  const ownTennisId = useSelector((state: AppState) => state.user.tennisId)
  const { match, matchId, type } = useModalState<
    ProfileModalsState,
    ProfileModalId.ACTION_PENDING_SCORE
  >(ProfileModalId.ACTION_PENDING_SCORE)
  const { modalDispatch, modalActions } = useModalDispatch<
    ProfileModalsState,
    ProfileModalId.ACTION_PENDING_SCORE
  >(ProfileModalId.ACTION_PENDING_SCORE)

  const modalConfig: Record<
    ActionPendingScoreModalType,
    {
      textHeader: string
      textBtnPrimary: string
      textBtnSecondary: string
      confirmAction: (matchId: string) => void
      bodyContent: () => ReactNode
    }
  > = useMemo(
    () => ({
      [ActionPendingScoreModalType.APPROVE]: {
        textHeader: i18n.t('strSocialMatches.modals.approve.header'),
        textBtnPrimary: i18n.t('strCommonUI.button.accept'),
        textBtnSecondary: i18n.t('strCommonUI.button.cancel'),
        confirmAction: approvePending,
        bodyContent: () => (
          <>
            <p className={styles.firstLetterCapital}>
              {i18n.t('strSocialMatches.modals.approve.info.1')}
            </p>
            <p className={styles.firstLetterCapital}>
              {i18n.t('strSocialMatches.modals.approve.info.2')}
            </p>
            <CheckboxWTN
              label={i18n.t('strSocialMatches.modals.approve.checkbox')}
              isChecked={isChecked}
              onChange={() => setIsChecked(prev => !prev)}
              className={styles.checkbox}
            />
          </>
        ),
      },
      [ActionPendingScoreModalType.REJECT]: {
        textHeader: i18n.t('strSocialMatches.modals.reject.header'),
        textBtnPrimary: i18n.t('strCommonUI.button.reject'),
        textBtnSecondary: i18n.t('strCommonUI.button.cancel'),
        confirmAction: rejectPending,
        bodyContent: () => (
          <>
            <p className={styles.firstLetterCapital}>
              {i18n.t('strSocialMatches.modals.reject.info.1')}
            </p>
            <p className={styles.firstLetterCapital}>
              {i18n.t('strSocialMatches.modals.reject.info.2')}
            </p>
            <CheckboxWTN
              label={i18n.t('strSocialMatches.modals.approve.checkbox')}
              isChecked={isChecked}
              onChange={() => setIsChecked(prev => !prev)}
              className={styles.checkbox}
            />
          </>
        ),
      },
      [ActionPendingScoreModalType.DELETE]: {
        textHeader: i18n.t('strSocialMatches.modals.delete.header'),
        textBtnPrimary: i18n.t('strCommonUI.button.yesDelete'),
        textBtnSecondary: i18n.t('strCommonUI.button.noCancel'),
        confirmAction: deletePending,
        bodyContent: () => {
          if (!match) return null
          const { submittedById, sides, start, type } = match
          const { s1Key, s2Key } = getSidesKeysRelativeToPlayer(submittedById, sides)

          return (
            <>
              <p className={styles.firstLetterCapital}>
                {i18n.t('strSocialMatches.modals.delete.info.1', {
                  datePlayed: moment(start).format('ddd, D MMM YYYY'),
                })}
              </p>
              <div className={styles.playersDetailsContainer}>
                <SidePlayers
                  sideNumber={1}
                  side={sides[s1Key]}
                  iconType={'avatar'}
                  isWinner={false}
                  truncateName={false}
                  matchType={type}
                  showRating={false}
                  showConfidence={false}
                  withLink={false}
                />
                vs
                <SidePlayers
                  sideNumber={2}
                  side={sides[s2Key]}
                  iconType={'avatar'}
                  isWinner={false}
                  truncateName={false}
                  matchType={type}
                  showRating={false}
                  showConfidence={false}
                  withLink={false}
                />
              </div>
              <p className={styles.noMargin}>{i18n.t('strSocialMatches.modals.delete.info.2')}</p>
            </>
          )
        },
      },
    }),
    [isChecked, match]
  )

  const handleConfirm = useCallback(() => {
    if (isChecked) {
      dontShowMeAgain(type, ownTennisId)
    }

    dispatch(modalConfig[type].confirmAction(matchId))
    setIsChecked(false)
    modalDispatch(modalActions.closeModal())
  }, [dispatch, isChecked, type, modalDispatch, modalActions, ownTennisId, matchId, modalConfig])

  const handleCancel = useCallback(() => {
    setIsChecked(false)
    modalDispatch(modalActions.closeModal())
  }, [modalDispatch, modalActions])

  return (
    <ModalWTN
      modalId={ProfileModalId.ACTION_PENDING_SCORE}
      widgetElId='player-profile'
      textHeader={modalConfig[type].textHeader}
      btnAlignment='right'
      btnPrimary={{
        text: modalConfig[type].textBtnPrimary,
        onClick: handleConfirm,
      }}
      btnSecondary={{
        text: modalConfig[type].textBtnSecondary,
        onClick: handleCancel,
      }}
    >
      {modalConfig[type].bodyContent()}
    </ModalWTN>
  )
}

export default ActionPendingScoreModal
